import { Component, OnDestroy, OnInit } from '@angular/core';
import { Permission, PermissionSubject, PermissionVerb, PermissionVerbs, TreeNode, TreeResource } from '@s8l/client-tree-lib';
import { Subscription } from 'rxjs';

import { CmsService } from 'src/app/services/cms.service';
import { Role } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';

export interface GeneralPermission {
  subject: PermissionSubject;
  description?: string;
  warning?: string;
  protect: boolean;
  any: PermissionVerb;
  all: PermissionVerb;
  conflict?: string;
}

@Component({
  selector: 'app-permission',
  templateUrl: './permission.component.html',
  styleUrls: ['./permission.component.scss']
})
export class PermissionComponent implements OnInit, OnDestroy {
  public roles: Role[];
  public subjects: PermissionSubject[];
  public selected: string[] = [];
  public disabled = false;

  public general: GeneralPermission[];

  // Node permission table: Determine state by basic node subject
  public readonly nodeSubject = PermissionSubject.Node;

  // Node permission table: Update basic node subjects and all resources except chat
  public readonly nodeUpdates = [
    PermissionSubject.Node,
    ...Object.values(TreeResource)
      .filter(r => r !== TreeResource.Chat)
      .map(r => PermissionSubject.Node.withSub(r.toLowerCase()))
  ];

  private crucialRoles: string[] = ['admin'];
  private crucialSubjects: string[] = [PermissionSubject.Role.key()];

  private descriptions: any = {
    node: 'read required for app access, write/delete for cms access',
    role: 'read required for cms access, write/delete for permission manipulation',
    user: 'read/write/delete required for user and permission management',
    stats: 'read required for user statistics, write for access control on statistics',
    booking: 'not used, replaced by "event" permission',
    licence: 'not used anymore, licence management can be accessed via user subject',
    node_settings: 'not used anymore'
  };

  private warnings: any = {
    user: 'Write access allows to assign roles to user accounts which can be used for privilege escalation. Grant it only to trusted users!',
    role: 'Write access allows to manipulate permissions which can be used for privilege escalation. Grant it only to trusted users!'
  };

  private subscription: Subscription;

  constructor(private users: UserService, private cms: CmsService) {}

  public ngOnInit() {
    this.subscription?.unsubscribe();
    this.subscription = this.users.roles.subscribe(roles => {
      this.roles = roles;
      this.subjects = this.cms.subjects.slice();
      if (this.selected.length == 0 && this.roles.length > 0) {
        this.selected.push(this.roles[0].name);
      }
      if (this.cms.legacyPermissionSystem) {
        this.descriptions.licence = 'read/write/delete required for licence & whitelist management';
        this.descriptions.node_settings = 'read/write/delete required notes & bookmark feature';
      }
      this.rebuildGeneralPermissions();
    });
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public permissionChanged(subject: PermissionSubject, event: { verb: PermissionVerb; state: boolean }) {
    this.disabled = true;
    const perm = new Permission(subject, event.verb);
    const prom = this.selected.map(role => this.cms.updateAccessVerb(role, event.state, null, [perm]));
    return Promise.all(prom)
      .then(() => this.rebuildGeneralPermissions())
      .finally(() => (this.disabled = false));
  }

  public rebuildGeneralPermissions() {
    if (this.selected.length === 0) {
      this.general = null;
      return;
    }
    this.general = this.subjects.map(subject => {
      const key = subject.key();
      const description = this.descriptions[key.replace('#', '_')];
      const warning = this.warnings[key.replace('#', '_')];
      const protect = this.crucialRoles.some(cr => this.selected.includes(cr)) && this.crucialSubjects.includes(key);
      const verbs = this.selected.map(role => this.cms.permissions.grants[role] ?? {}).map(verbs => verbs[key] ?? 0);
      const any = verbs.reduce((a, b) => a | b) as PermissionVerb;
      const all = verbs.reduce((a, b) => a & b) as PermissionVerb;
      const conflict = this.getConflict(any);
      return { subject, protect, description, warning, any, all, conflict };
    });
  }

  private getConflict(verbs: PermissionVerb): string {
    const r = PermissionVerbs.has(verbs, PermissionVerb.Read);
    const w = PermissionVerbs.has(verbs, PermissionVerb.Write);
    const d = PermissionVerbs.has(verbs, PermissionVerb.Delete);
    if (d && !(r && w)) {
      return "for delete access 'read' and 'write' should be enabled as well";
    } else if (w && !r) {
      return "for write access 'read' should be enabled as well";
    } else {
      return null;
    }
  }
}
